import React, { useCallback, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useDropzone } from "react-dropzone";
import OverlayLoading from "../components/OverlayLoading";
import Title from "../components/title";
import DropzoneComponent from "../components/Utils/DropzoneComponent";
import html2canvas from "html2canvas";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFile, faFileCsv, faUpload } from "@fortawesome/free-solid-svg-icons";
import { apiFetch } from "../apiClient";
import { colors } from "../global";
import { GeneralInformation } from "../components/PricingPowerpoint/GeneralInformation";
import MixedLaneChart from "../components/PricingPowerpoint/SummaryTop10LanesChart";
import PieChart from "../components/PricingPowerpoint/PieChart";
import ReceivingTable from "../components/PricingPowerpoint/ReceivingTable";
import { OverviewSection } from "../components/PricingPowerpoint/OverviewSection";

const toCamelCase = (str) =>
  str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());

const PricingPowerpoint = () => {
  const [files, setFiles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dashboardData, setDashboardData] = useState({});
  const chartKeys = [
    "general_information",
    "summary_top_lane_by_spend",
    "summary_by_mode",
    "summary_by_to_state",
    "receiving_table_by_name",
    "receiving_table_by_state",
    "receiving_table_by_unit_type",
    "receiving_table_by_carrier",
    "receiving_table_by_cora_carrier",
  ];

  const chartRefs = useRef(
    chartKeys.reduce((acc, key) => {
      acc[toCamelCase(key)] = React.createRef();
      return acc;
    }, {})
  ).current;
  const getRef = (k) => chartRefs[toCamelCase(k)];

  const radarChartRefs = useRef({});

  const onDrop = useCallback((acceptedFiles) => {
    setFiles(acceptedFiles);
  }, []);
  const handleUpload = async () => {
    if (files.length === 0) {
      toast.error("No files selected");
      return;
    }
    setIsSubmitting(true);

    try {
      const formData = new FormData();

      formData.append("file", files[0]);

      const response = await apiFetch(`/api/v1/pricingPowerpoint/upload`, {
        method: "POST",
        body: formData,
        credentials: "include",
      });

      if (!response.ok) {
        let errorMessage = "Upload failed.";
        try {
          const errorResponse = await response.json();
          errorMessage =
            errorResponse.detail || errorResponse.error || errorMessage;
        } catch (err) {
          console.error("Failed to parse error response", err);
        }
        toast.error(errorMessage);
        return;
      }

      const result = await response.json();
      setDashboardData(result);
      toast.success("Upload successful");
      setFiles([]);
    } catch (err) {
      toast.error("Upload failed. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ".csv",
    multiple: true,
  });

  const handleExportAllChartsToAPI = async (slideExportData) => {
    setIsSubmitting(true);
    try {
      const formData = new FormData();

      const chartEntries = chartKeys.map((key) => ({
        name: key,
        ref: chartRefs[toCamelCase(key)],
      }));

      console.log(chartKeys);
      console.log(chartEntries);

      for (const { name, ref } of chartEntries) {
        if (!ref || !ref.current) {
          console.warn(`Skipping "${name}" - ref not found or not mounted`);
          continue;
        }

        try {
          const canvas = await html2canvas(ref.current, {
            useCORS: true,
            scale: 2,
          });

          if (!canvas) {
            console.error(`html2canvas failed for ${name}`);
            continue;
          }

          const blob = await new Promise((resolve) =>
            canvas.toBlob(resolve, "image/png")
          );

          if (blob) {
            formData.append("images", blob, `${name}.png`);
          } else {
            console.error(`Failed to create blob for ${name}`);
          }
        } catch (err) {
          console.error(`Error exporting chart "${name}":`, err);
        }
      }

      for (const [owner, radarRef] of Object.entries(radarChartRefs.current)) {
        if (!radarRef?.current) continue;

        const canvas = await html2canvas(radarRef.current, {
          useCORS: true,
          scale: 2,
        });
        const blob = await new Promise((resolve) =>
          canvas.toBlob(resolve, "image/png")
        );

        if (blob) {
          formData.append("images", blob, `radar_${owner}.png`);
        }
      }

      const response = await apiFetch(`/api/v1/pricingPowerpoint/generate`, {
        method: "POST",
        body: formData,
        credentials: "include",
      });

      if (!response.ok) {
        let errorMessage = "Upload failed.";
        try {
          const errorResponse = await response.json();
          if (errorResponse.detail) {
            errorMessage = errorResponse.detail;
          } else if (errorResponse.error) {
            errorMessage = errorResponse.error;
          }
        } catch (parseError) {
          console.error("Failed to parse error response", parseError);
        }

        toast.error(errorMessage);
        return;
      }

      const contentType = response.headers.get("Content-Type");

      if (
        response.ok &&
        contentType &&
        contentType.includes(
          "application/vnd.openxmlformats-officedocument.presentationml.presentation"
        )
      ) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;

        const timestamp = new Date().toISOString().replace(/[:.]/g, "-"); // optional
        link.setAttribute("download", `Slide_Presentation_${timestamp}.pptx`);

        document.body.appendChild(link);
        link.click();
        link.remove();
      } else {
        const errorText = await response.text();
        console.error("Download failed. Response not PowerPoint:", errorText);
        toast.error(
          "Upload succeeded, but the server did not return a .pptx file."
        );
      }
    } catch (err) {
      console.error("Error uploading charts:", err);
      toast.error("Failed to upload charts.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const getFileIcon = (fileName) => {
    const extension = fileName.split(".").pop().toLowerCase();
    switch (extension) {
      case "csv":
        return <FontAwesomeIcon icon={faFileCsv} />;
      default:
        return <FontAwesomeIcon icon={faFile} />;
    }
  };
  function currency(n, currency = "AUD") {
    if (n == null || n === "") return "—";
    const num = Number(n);
    if (isNaN(num)) return String(n);
    return num.toLocaleString(undefined, {
      style: "currency",
      currency,
      maximumFractionDigits: 2,
    });
  }
  return (
    <>
      {isSubmitting && (
        <OverlayLoading
          isVisible={isSubmitting}
          message="Processing your request..."
          icon="truck"
        />
      )}

      <div className="p-6 pb-0">
        <Title title={"Pricing Powerpoint"} />
      </div>

      <div className="m-4 p-6 bg-white rounded-xl border border-gray-100 shadow-sm">
        <DropzoneComponent
          getInputProps={getInputProps}
          getRootProps={getRootProps}
          isDragActive={isDragActive}
          fileTypes="XLSX, XLSM"
        />

        {files.length > 0 && (
          <div className="mt-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">
                Selected Files ({files.length})
              </h3>
              <div className="text-sm text-gray-500">
                Total:{" "}
                {formatFileSize(
                  files.reduce((acc, file) => acc + file.size, 0)
                )}
              </div>
            </div>
            <ul className="list-disc pl-5">
              {files.map((file, index) => (
                <div
                  key={file.name + index}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors mt-3"
                >
                  <div className="flex items-center space-x-3 flex-1 min-w-0">
                    {getFileIcon(file.name)}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {file.name}
                        </p>
                      </div>
                      <p className="text-xs text-gray-500">
                        {formatFileSize(file.size)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </ul>

            <button
              onClick={handleUpload}
              className="mt-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              disabled={isSubmitting}
            >
              <FontAwesomeIcon icon={faUpload} className="mr-2" /> Upload All
            </button>
          </div>
        )}
      </div>
      <div className="p-4 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {dashboardData?.general_information && (
          <div className="w-full bg-white p-4 rounded-lg shadow col-span-2">
            <OverviewSection
              general_information={dashboardData?.general_information}
            />
          </div>
        )}
        {dashboardData?.general_information && (
          <div
            className="w-full bg-white p-3 py-8 rounded-lg shadow col-span-2"
            ref={getRef("general_information")}
          >
            <GeneralInformation
              general_information={dashboardData?.general_information}
            />
          </div>
        )}
        {dashboardData?.summary_top_lane_by_spend && (
          <div
            className="w-full bg-white p-4 rounded-lg shadow"
            ref={getRef("summary_top_lane_by_spend")}
          >
            <MixedLaneChart data={dashboardData?.summary_top_lane_by_spend} />
          </div>
        )}
        {dashboardData?.summary_by_mode && (
          <div
            className="w-full bg-white p-4 rounded-lg shadow"
            ref={getRef("summary_by_mode")}
          >
            <PieChart data={dashboardData?.summary_by_mode} />
          </div>
        )}
        {dashboardData?.summary_by_to_state && (
          <div
            className="w-full bg-white p-4 rounded-lg shadow"
            ref={getRef("summary_by_to_state")}
          >
            <PieChart
              data={dashboardData?.summary_by_to_state}
              labelKey="To State"
              valueKey="value"
              title="Receiving States"
            />
          </div>
        )}
        {dashboardData?.receiving_table_by_cora_carrier && (
          <div
            className="w-full bg-white p-4 rounded-lg shadow"
            ref={getRef("receiving_table_by_cora_carrier")}
          >
            <PieChart
              data={dashboardData?.receiving_table_by_cora_carrier}
              labelKey="Cora_Carrier"
              valueKey="# Cons"
              title="Optimised Carrier"
            />
          </div>
        )}
        {dashboardData?.receiving_table_by_name && (
          <div
            className="w-full bg-white p-4 rounded-lg shadow"
            ref={getRef("receiving_table_by_name")}
          >
            <ReceivingTable
              title="Receiving By Name"
              data={dashboardData?.receiving_table_by_name}
              columns={[
                { key: "To Name", label: "Receiving", sortable: true },
                {
                  key: "# Cons",
                  label: "# Cons",
                  align: "right",
                  numeric: true,
                },
                {
                  key: "Cost Per Kg",
                  label: "Cost Per Kg",
                  align: "right",
                  numeric: true,
                  render: (v) => currency(v),
                },
                {
                  key: "Incumbent Charge",
                  label: "Incumbent Charge",
                  align: "right",
                  numeric: true,
                  render: (v) => currency(v),
                },
              ]}
            />
          </div>
        )}
        {dashboardData?.receiving_table_by_state && (
          <div
            className="w-full bg-white p-4 rounded-lg shadow"
            ref={getRef("receiving_table_by_state")}
          >
            <ReceivingTable
              title="Receiving By State"
              data={dashboardData?.receiving_table_by_state}
              columns={[
                { key: "To State", label: "Receiving", sortable: true },
                {
                  key: "# Cons",
                  label: "# Cons",
                  align: "right",
                  numeric: true,
                },
                {
                  key: "Cost Per Kg",
                  label: "Cost Per Kg",
                  align: "right",
                  numeric: true,
                  render: (v) => currency(v),
                },
                {
                  key: "Incumbent Charge",
                  label: "Incumbent Charge",
                  align: "right",
                  numeric: true,
                  render: (v) => currency(v),
                },
              ]}
            />
          </div>
        )}
        {dashboardData?.receiving_table_by_unit_type && (
          <div
            className="w-full bg-white p-4 rounded-lg shadow"
            ref={getRef("receiving_table_by_unit_type")}
          >
            <ReceivingTable
              title="Receiving By Unit Type"
              data={dashboardData?.receiving_table_by_unit_type}
              columns={[
                { key: "Unit Type", label: "Unit Type", sortable: true },
                {
                  key: "# Cons",
                  label: "# Cons",
                  align: "right",
                  numeric: true,
                },
                {
                  key: "Cost Per Kg",
                  label: "Cost Per Kg",
                  align: "right",
                  numeric: true,
                  render: (v) => currency(v),
                },
                {
                  key: "Incumbent Charge",
                  label: "Incumbent Charge",
                  align: "right",
                  numeric: true,
                  render: (v) => currency(v),
                },
              ]}
            />
          </div>
        )}
        {dashboardData?.receiving_table_by_carrier && (
          <div
            className="w-full bg-white p-4 rounded-lg shadow"
            ref={getRef("receiving_table_by_carrier")}
          >
            <ReceivingTable
              title="Receiving By Carrier"
              data={dashboardData?.receiving_table_by_carrier}
              columns={[
                { key: "Carrier", label: "Carrier", sortable: true },
                {
                  key: "# Cons",
                  label: "# Cons",
                  align: "right",
                  numeric: true,
                },
                {
                  key: "Cost Per Kg",
                  label: "Cost Per Kg",
                  align: "right",
                  numeric: true,
                  render: (v) => currency(v),
                },
                {
                  key: "Incumbent Charge",
                  label: "Incumbent Charge",
                  align: "right",
                  numeric: true,
                  render: (v) => currency(v),
                },
              ]}
            />
          </div>
        )}
      </div>
      {dashboardData?.general_information && (
        <div className="mt-4 mx-4 flex justify-end">
          <button
            onClick={handleExportAllChartsToAPI}
            className="px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50"
            style={{ backgroundColor: colors.success, color: colors.white }}
          >
            Export PowerPoint
          </button>
        </div>
      )}
    </>
  );
};

export default PricingPowerpoint;
